import { z } from 'zod';

/**
 * 시딩 캠페인(모집형) 상세 화면 상태 — 문서 "화면 상태 결정 로직" 11종.
 *
 * BE 는 raw 상태값만 응답하고, FE 가 `participation.status` + `fulfillments[].status`
 * + `contentSubmissions` + `goods[].soldOut` 조합으로 화면을 분기한다.
 */
export const SEEDING_RECRUITMENT_SCREEN_STATE = {
  /** 신청 전 (탐색) — participation = null */
  BEFORE_APPLY: 'BEFORE_APPLY',
  /** 신청 후 — PENDING + recruitmentEndAt 미경과 */
  APPLIED: 'APPLIED',
  /** 모집 종료·선정 대기 — PENDING + recruitmentEndAt 경과 + announcementAt 미경과 */
  AWAITING_SELECTION: 'AWAITING_SELECTION',
  /** 선정됨 (상품 준비 중) — CONFIRMED + 전체 SHIPPING_READY */
  SELECTED_PREPARING: 'SELECTED_PREPARING',
  /** 배송 중 — CONFIRMED + 가장 낮은 단계 = SHIPPING */
  SHIPPING: 'SHIPPING',
  /** 배송 완료 — CONFIRMED + 전체 DELIVERED + contentSubmissions empty */
  DELIVERED: 'DELIVERED',
  /** 콘텐츠 등록 완료 — COMPLETED + 전체 DELIVERED + contentSubmissions not empty */
  CONTENT_SUBMITTED: 'CONTENT_SUBMITTED',
  /** 미선정 — NOT_SELECTED */
  NOT_SELECTED: 'NOT_SELECTED',
  /** 자진 취소 — SELF_WITHDRAWN */
  SELF_WITHDRAWN: 'SELF_WITHDRAWN',
  /** 콘텐츠 미제출 종료 — EXPIRED + 전체 DELIVERED + contentSubmissions empty */
  CONTENT_EXPIRED: 'CONTENT_EXPIRED',
  /** 전체 품절·진행 불가 — participation = null + 전체 goods 품절 */
  SOLD_OUT_UNAVAILABLE: 'SOLD_OUT_UNAVAILABLE',
} as const;

export const SeedingRecruitmentScreenStateSchema = z
  .enum([
    SEEDING_RECRUITMENT_SCREEN_STATE.BEFORE_APPLY,
    SEEDING_RECRUITMENT_SCREEN_STATE.APPLIED,
    SEEDING_RECRUITMENT_SCREEN_STATE.AWAITING_SELECTION,
    SEEDING_RECRUITMENT_SCREEN_STATE.SELECTED_PREPARING,
    SEEDING_RECRUITMENT_SCREEN_STATE.SHIPPING,
    SEEDING_RECRUITMENT_SCREEN_STATE.DELIVERED,
    SEEDING_RECRUITMENT_SCREEN_STATE.CONTENT_SUBMITTED,
    SEEDING_RECRUITMENT_SCREEN_STATE.NOT_SELECTED,
    SEEDING_RECRUITMENT_SCREEN_STATE.SELF_WITHDRAWN,
    SEEDING_RECRUITMENT_SCREEN_STATE.CONTENT_EXPIRED,
    SEEDING_RECRUITMENT_SCREEN_STATE.SOLD_OUT_UNAVAILABLE,
  ])
  .describe('SeedingRecruitmentScreenState');

export type SeedingRecruitmentScreenState = z.infer<typeof SeedingRecruitmentScreenStateSchema>;

// BE 정식값은 TWENTY_NINE_CM. 29CM 도 수용 후 29CM 로 정규화(상세 응답 platform 은 미소비 — 서비스 분기는 device service 사용).
const PlatformSchema = z
  .enum(['MUSINSA', '29CM', 'TWENTY_NINE_CM'])
  .transform((value) => (value === 'TWENTY_NINE_CM' ? '29CM' : value))
  .describe('SeedingPlatform');

const CampaignStatusSchema = z.enum(['ACTIVE', 'INACTIVE']).describe('SeedingCampaignStatus');

const SnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('SeedingSnsChannel');

const ContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('SeedingContentType');

const ContentTypeOptionSchema = z
  .object({
    type: ContentTypeSchema,
    uploadDeadlineDays: z.number().int(),
  })
  .describe('SeedingContentTypeOption');

export const SeedingCampaignSchema = z
  .object({
    id: z.number().int(),
    platform: PlatformSchema,
    brandId: z.string(),
    // 표시용 브랜드명(예: '032씨'). brandId 는 코드('032c'). 응답엔 있으나 문서 미선언이라 nullish.
    brandName: z.string().nullish(),
    title: z.string(),
    status: CampaignStatusSchema,
    snsChannels: z.array(SnsChannelSchema),
    contentTypes: z.array(ContentTypeOptionSchema),
    requiredHashtags: z.array(z.string()),
    /** `requiredHashtags`(각 항목에 '#' 포함) 를 공백 join 한 표시·복사용 문자열 (service 가 derive). */
    hashtagText: z.string(),
    requestNote: z.string().nullable(),
    maxParticipants: z.number().int(),
    recruitmentStartAt: z.string(),
    recruitmentEndAt: z.string(),
    announcementAt: z.string(),
  })
  .describe('SeedingCampaign');

export type SeedingCampaign = z.infer<typeof SeedingCampaignSchema>;

export const SeedingGoodsItemSchema = z
  .object({
    id: z.number().int(),
    goodsNo: z.number().int(),
    slotNo: z.number().int(),
    category: z.string(),
    brandName: z.string(),
    goodsName: z.string(),
    imageUrl: z.string(),
    // 상품 PDP 랜딩 URL — 썸네일 클릭 시 이동 (완성된 절대 URL). (QAT-39446)
    webUrl: z.string(),
    price: z.number().int(),
    soldOut: z.boolean(),
    // 큐레이터가 이 goods 를 선택했는지(옵션 없는 단품도 선택 시 true). 신청/수락 전이면 false. (QAT-39515)
    selected: z.boolean(),
    // 신청/수락 시 선택한 옵션 표시 문자열(예: '라이트 베이지 · L'). 미선택·단품이면 null. (QAT-39515)
    selectedOption: z.string().nullish(),
  })
  .describe('SeedingGoodsItem');

export type SeedingGoodsItem = z.infer<typeof SeedingGoodsItemSchema>;

const ParticipationStatusSchema = z
  .enum([
    'PENDING',
    'CONFIRMED',
    'NOT_SELECTED',
    'SELF_WITHDRAWN',
    'EXPIRED',
    'COMPLETED',
    'REJECTED',
    'AUTO_TIMEOUT',
  ])
  .describe('SeedingParticipationStatus');

const ParticipationTypeSchema = z.enum(['RECRUITMENT']).describe('SeedingParticipationType');

export const SeedingParticipationSchema = z
  .object({
    id: z.number().int(),
    status: ParticipationStatusSchema,
    participationType: ParticipationTypeSchema,
    selectedContentType: ContentTypeSchema.nullable(),
    messageToBrand: z.string().nullable(),
    contentSubmissionDeadlineAt: z.string().nullable(),
    confirmedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    rejectionReasons: z.array(z.string()).nullable(),
  })
  .describe('SeedingParticipation');

export type SeedingParticipation = z.infer<typeof SeedingParticipationSchema>;

const FulfillmentStatusSchema = z
  .enum(['SHIPPING_READY', 'SHIPPING', 'DELIVERED', 'CANCELLED'])
  .describe('SeedingFulfillmentStatus');

export const SeedingFulfillmentSchema = z
  .object({
    id: z.number().int(),
    orderId: z.string(),
    orderOptionId: z.number().int(),
    status: FulfillmentStatusSchema,
    shippingDeadlineAt: z.string(),
    shippedAt: z.string().nullable(),
    deliveredAt: z.string().nullable(),
    // 택배사명/송장번호/배송추적URL — 송장 등록 시 노출(배송준비중 포함), 미등록·취소·미지원 시 null. (API 0610 SeedingDetailFulfillment)
    courierName: z.string().nullish(),
    invoiceNo: z.string().nullish(),
    trackingUrl: z.string().nullish(),
  })
  .describe('SeedingFulfillment');

export type SeedingFulfillment = z.infer<typeof SeedingFulfillmentSchema>;

export const SeedingContentSubmissionSchema = z
  .object({
    id: z.number().int(),
    channel: SnsChannelSchema,
    contentType: ContentTypeSchema,
    contentUrl: z.string(),
  })
  .describe('SeedingContentSubmissionItem');

export type SeedingContentSubmission = z.infer<typeof SeedingContentSubmissionSchema>;

const DeliveryAddressSourceSchema = z
  .enum(['DEFAULT', 'ORDER_SNAPSHOT'])
  .describe('SeedingDeliveryAddressSource');

export const SeedingDeliveryAddressSchema = z
  .object({
    source: DeliveryAddressSourceSchema,
    // 배송지 별칭('우리집' 등). ORDER_SNAPSHOT 만 보유 / DEFAULT 는 null
    addressName: z.string().nullable(),
    name: z.string(),
    phone: z.string(),
    address1: z.string(),
    address2: z.string().nullable(),
    // 0610 추가 — 배송지 id / 우편번호 / 메모 (null 가능)
    deliveryAddressId: z.number().int().nullable().optional(),
    zipcode: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
  })
  .describe('SeedingDeliveryAddress');

export type SeedingDeliveryAddress = z.infer<typeof SeedingDeliveryAddressSchema>;

export const SeedingPenaltySchema = z
  .object({
    // 응답에 포함되면 항상 활성 패널티로 간주 (active 플래그 없음)
    reason: z.string(),
  })
  .describe('SeedingPenalty');

export type SeedingPenalty = z.infer<typeof SeedingPenaltySchema>;

export const GetSeedingRecruitmentDetailServiceRequestSchema = z
  .object({
    campaignId: z.number().int(),
  })
  .describe('GetSeedingRecruitmentDetailServiceRequest');

export type GetSeedingRecruitmentDetailServiceRequest = z.infer<
  typeof GetSeedingRecruitmentDetailServiceRequestSchema
>;

export const GetSeedingRecruitmentDetailServiceResponseSchema = z
  .object({
    /** 화면 상태 결정 로직으로 도출한 11종 화면 enum */
    screenState: SeedingRecruitmentScreenStateSchema,
    campaign: SeedingCampaignSchema,
    goods: z.array(SeedingGoodsItemSchema),
    participation: SeedingParticipationSchema.nullable(),
    fulfillments: z.array(SeedingFulfillmentSchema),
    contentSubmissions: z.array(SeedingContentSubmissionSchema),
    deliveryAddress: SeedingDeliveryAddressSchema.nullable(),
    penalty: SeedingPenaltySchema.nullable(),
    /** 발송 기한 영업일 수 N ("선정일로부터 영업일 N일 이내"). BE 정책 상수. (API 0615) */
    shippingDeadlineDays: z.number().int().nullable(),
    /** 예상 발송 마감일 (KST). 모집형=선정 발표일+N영업일. 공휴일 조회 실패 시 null. (API 0615) */
    estimatedShippingDeadlineAt: z.string().nullable(),
  })
  .describe('GetSeedingRecruitmentDetailServiceResponse');

export type GetSeedingRecruitmentDetailServiceResponse = z.infer<
  typeof GetSeedingRecruitmentDetailServiceResponseSchema
>;
